import { IBanner } from '@/types/banners'
import { hasPlans } from '@/constants/hasPlans'
import { hasOrderStatus } from '@/constants/hasOrderStatus'
import { isInRouteScope } from '@/utils/routes'
import type { Order } from '@/types/commerce'

interface IBannerSelectionOptions {
  ids?: string[]
  maxVisible?: number
  now?: Date
  orders?: Order[]
  // Plan-ruled banners stay hidden until orders are resolved, so a banner
  // aimed at non-subscribers never flashes for a subscriber while loading.
  ordersReady?: boolean
  // Storage-backed dismissal check (see isBannerDismissed in useBanner).
  // Dismissed banners are skipped so their slot goes to the next eligible one.
  isDismissed?: (banner: IBanner) => boolean
  // Resolved user country (ISO code) for geo-ruled banners. Undefined when the
  // lookup failed/is unresolvable — treated as fail-open (see matchesGeo).
  country?: string
  // Geo-ruled banners stay hidden until geolocation settles (resolved OR
  // errored), so a geo-gated banner never flashes for a restricted user while
  // loading. Mirrors the ordersReady gate for plan rules.
  geoReady?: boolean
  // Whether the visitor is authenticated. Absent reads as logged out, so a
  // banner carrying an order-state rule stays hidden (fail closed).
  isLoggedIn?: boolean
  // Current route, matched against the scope of self-targeted banners. Absent
  // hides them (fail closed) — an unknown route is never "every route".
  pathname?: string
  // Whether the current scene declares a banners block at all. Banners without
  // their own scope only appear on scenes that do (see matchesPlacement).
  hasSceneBanners?: boolean
}

const DEFAULT_MAX_VISIBLE = 1

// Keep in sync with the renderer switch in components/UI/Banner. Types
// without a renderer (e.g. 'form') must not consume a visibility slot.
const RENDERABLE_TYPES: IBanner['type'][] = ['notice', 'offer']

const toTime = (isoDate?: string): number | undefined => {
  if (!isoDate) return undefined
  const time = new Date(isoDate).getTime()
  // Malformed dates are ignored (fail-open) rather than hiding the banner
  return Number.isNaN(time) ? undefined : time
}

const isWithinSchedule = (banner: IBanner, now: Date): boolean => {
  const startsAt = toTime(banner.startsAt)
  const endsAt = toTime(banner.endsAt)
  const time = now.getTime()
  if (startsAt !== undefined && time < startsAt) return false
  if (endsAt !== undefined && time > endsAt) return false
  return true
}

// Authentication state, targeted directly — unlike a plan rule, which answers
// "holds no active plan" and so also covers a logged-in non-subscriber.
const matchesAudience = (banner: IBanner, options: IBannerSelectionOptions): boolean => {
  if (!banner.audience) return true
  return banner.audience === 'authenticated' ? !!options.isLoggedIn : !options.isLoggedIn
}

export const isSelfTargeted = (banner: IBanner): boolean => banner.scope !== undefined

// Placement is either banner-driven or scene-driven, never both: declaring a
// scope opts the banner out of the scene's ids allowlist and onto its own
// routes. Scene-targeted banners additionally require the scene to declare a
// banners block — the ids filter is deliberately open (absent ids = every
// banner), so without that term a home-only promo would render on every scene
// that stays silent about banners.
const matchesPlacement = (banner: IBanner, options: IBannerSelectionOptions): boolean => {
  if (isSelfTargeted(banner)) {
    if (options.pathname === undefined) return false
    return isInRouteScope(banner.scope?.routes, banner.scope?.excludeRoutes, options.pathname)
  }
  if (!options.hasSceneBanners) return false
  return !options.ids || options.ids.includes(banner._id)
}

export const hasPlanRule = (banner: IBanner): boolean =>
  !!(banner.plans?.inclusive?.length || banner.plans?.exclusive?.length)

const matchesPlans = (banner: IBanner, options: IBannerSelectionOptions): boolean => {
  if (!hasPlanRule(banner)) return true
  if (!options.ordersReady) return false
  return hasPlans(options.orders, banner.plans)
}

export const hasOrderStatusRule = (banner: IBanner): boolean =>
  !!(banner.orderStatus?.inclusive?.length || banner.orderStatus?.exclusive?.length)

const matchesOrderStatus = (banner: IBanner, options: IBannerSelectionOptions): boolean => {
  if (!hasOrderStatusRule(banner)) return true
  // Checked before ordersReady on purpose: that gate is open for anonymous
  // visitors (they have no orders to wait for), so an exclusive rule would
  // otherwise pass with no orders at all and show a billing-state banner to
  // someone who has no account.
  if (!options.isLoggedIn) return false
  if (!options.ordersReady) return false
  return hasOrderStatus(options.orders, banner.orderStatus)
}

export const hasGeoRule = (banner: IBanner): boolean =>
  !!(banner.geolocation?.allow?.length || banner.geolocation?.deny?.length)

// Mirrors getGeolocationStatus in useGeolocation (allow/deny + fail-open),
// but operates on the resolved country string injected by the caller so this
// stays a pure function (no hook). Keep the two in sync.
const matchesGeo = (banner: IBanner, options: IBannerSelectionOptions): boolean => {
  if (!hasGeoRule(banner)) return true
  if (!options.geoReady) return false
  // Unknown country (lookup failed / unresolvable, e.g. iCloud Private Relay):
  // fail open — a geo outage must not hide a banner (FZSPF-3792).
  const country = options.country
  if (!country) return true
  const { allow = [], deny = [] } = banner.geolocation ?? {}
  if (deny.includes(country)) return false
  return allow.length > 0 ? allow.includes(country) : true
}

export const getEligibleBanners = (
  banners: IBanner[],
  options: IBannerSelectionOptions = {}
): IBanner[] => {
  const { maxVisible = DEFAULT_MAX_VISIBLE, now = new Date(), isDismissed } = options
  const eligible = banners
    .filter((banner) => banner.isActive)
    .filter((banner) => RENDERABLE_TYPES.includes(banner.type))
    .filter((banner) => isWithinSchedule(banner, now))
    .filter((banner) => matchesAudience(banner, options))
    .filter((banner) => matchesPlacement(banner, options))
    .filter((banner) => matchesPlans(banner, options))
    .filter((banner) => matchesOrderStatus(banner, options))
    .filter((banner) => matchesGeo(banner, options))
    .filter((banner) => !isDismissed?.(banner))
    .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))

  // Offer banners self-gate at render time (they only show while a special
  // offer is active), so they pass through without consuming a slot —
  // otherwise an inactive offer would blank the header while an eligible
  // notice banner sits sliced out.
  const selected: IBanner[] = []
  let slots = maxVisible
  for (const banner of eligible) {
    if (banner.type === 'offer') {
      selected.push(banner)
    } else if (slots > 0) {
      selected.push(banner)
      slots -= 1
    }
  }
  return selected
}

/**
 * Whether the banner strip renders at all, given the scene's banners config
 * and the banners already selected for it. A scene that explicitly disables
 * banners shows none, whatever their scope — that is the scene-level kill
 * switch and it wins over everything. Otherwise the strip renders when the
 * scene enables it, or when a self-targeted banner was selected on a scene
 * that simply stays silent about banners.
 */
export const shouldRenderBannerStrip = (
  sceneBanners: { hasBanners?: boolean } | undefined,
  selected: IBanner[]
): boolean => {
  if (sceneBanners?.hasBanners === false) return false
  return !!sceneBanners?.hasBanners || selected.some(isSelfTargeted)
}
